import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import type { GridPaginationModel } from '@mui/x-data-grid-pro'
import { PageHeader } from 'src/components/payments-ops/PageHeader'
import { EarningOrdersModal } from 'src/components/payments-ops/us-bank-transactions/EarningOrdersModal'
import { UsBankEarningsTable } from 'src/components/payments-ops/us-bank-transactions/UsBankEarningsTable'
import { UsBankFiltersToolbar } from 'src/components/payments-ops/us-bank-transactions/UsBankFiltersToolbar'
import { AdminFetchUsBankEarnings } from 'src/constants/actionTypes'
import { PAYMENTS_OPS_DEFAULT_PAGE_SIZE } from 'src/constants/payments-ops/constants'
import { EarningOrderStatus, UsBankDestinationType } from 'src/constants/payments-ops/enums'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import type { UsBankEarning, UsBankEarningFilters } from 'src/interfaces/usBankTransactions'
import { getUsBankEarningsError } from 'src/selectors/usBankTransactions'
import { useQueryParamUpdater } from 'src/utils/hooks/paymentsOps/useQueryParamUpdater'
import { isUuid } from 'src/utils/partnerDriver/isUuid'

const DESTINATIONS = Object.values(UsBankDestinationType) as string[]
const ORDER_STATUSES = Object.values(EarningOrderStatus) as string[]

const FILTER_KEYS: Array<keyof UsBankEarningFilters> = [
  'contractorId',
  'contractorExternalId',
  'partnerDisplayName',
  'destinationType',
  'orderStatus',
  'uploadedFrom',
  'uploadedTo',
  'activeOnly',
]

/** The query-string form of one filter; `null` means the key is absent. */
const toParamValue = (
  key: keyof UsBankEarningFilters,
  value: UsBankEarningFilters[keyof UsBankEarningFilters],
): string | null => {
  if (key === 'activeOnly') {
    return value ? 'true' : null
  }

  return (value as string | null) ?? null
}

const UsBankTransactionsPageComponent = () => {
  const [searchParams] = useSearchParams()
  const updateParams = useQueryParamUpdater()
  const { isAuthenticated } = useAuthSession()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const listError = useSelector(getUsBankEarningsError)

  const [ordersEarning, setOrdersEarning] = useState<UsBankEarning | null>(null)

  const filters = useMemo<UsBankEarningFilters>(() => {
    const destination = searchParams.get('destinationType') ?? ''
    const orderStatus = searchParams.get('orderStatus') ?? ''
    const contractorId = searchParams.get('contractorId') ?? undefined

    return {
      // A hand-edited or stale non-UUID id would 400 every load (`@IsUUID()`), so it is ignored.
      contractorId: isUuid(contractorId) ? contractorId : null,
      contractorExternalId: searchParams.get('contractorExternalId'),
      partnerDisplayName: searchParams.get('partnerDisplayName'),
      destinationType: DESTINATIONS.includes(destination)
        ? (destination as UsBankDestinationType)
        : null,
      orderStatus: ORDER_STATUSES.includes(orderStatus)
        ? (orderStatus as EarningOrderStatus)
        : null,
      uploadedFrom: searchParams.get('uploadedFrom'),
      uploadedTo: searchParams.get('uploadedTo'),
      activeOnly: searchParams.get('activeOnly') === 'true',
    }
  }, [searchParams])

  const paginationModel = useMemo(() => {
    const pageSize = Number(searchParams.get('limit')) || PAYMENTS_OPS_DEFAULT_PAGE_SIZE
    const offset = Number(searchParams.get('offset')) || 0

    return { page: Math.floor(offset / pageSize), pageSize }
  }, [searchParams])

  useEffect(() => {
    if (!isAuthenticated) {
      return
    }

    asyncDispatchWithErrorToast(
      AdminFetchUsBankEarnings.request({
        ...filters,
        limit: paginationModel.pageSize,
        offset: paginationModel.page * paginationModel.pageSize,
      }),
    )
  }, [asyncDispatchWithErrorToast, filters, isAuthenticated, paginationModel])

  const handleFiltersChange = useCallback(
    (updates: Partial<UsBankEarningFilters>) => {
      const changedParams: Record<string, string | null> = {}

      FILTER_KEYS.forEach((key) => {
        if (!(key in updates)) {
          return
        }

        const nextValue = toParamValue(key, updates[key] ?? null)

        if (nextValue !== toParamValue(key, filters[key])) {
          changedParams[key] = nextValue
        }
      })

      // A no-op (e.g. Apply with nothing edited) must not rewrite the URL or reset the page.
      if (Object.keys(changedParams).length === 0) {
        return
      }

      updateParams({ ...changedParams, offset: null })
    },
    [filters, updateParams],
  )

  const handleClear = useCallback(() => {
    const presentKeys = FILTER_KEYS.filter((key) => {
      return searchParams.has(key)
    })

    // Nothing to clear: leave the URL (and the current page) alone.
    if (presentKeys.length === 0) {
      return
    }

    const cleared = presentKeys.reduce<Record<string, string | null>>((next, key) => {
      return { ...next, [key]: null }
    }, {})

    updateParams({ ...cleared, offset: null })
  }, [searchParams, updateParams])

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      updateParams({ limit: String(model.pageSize), offset: String(model.page * model.pageSize) })
    },
    [updateParams],
  )

  const handleCloseOrders = useCallback(() => {
    setOrdersEarning(null)
  }, [])

  return (
    <div
      data-testid="us-bank-transactions-page"
      css={tw`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-6`}
    >
      <PageHeader
        title="US Bank Transactions"
        description="Earnings with their latest US Bank order. Net order amount is after fees."
      />
      <UsBankFiltersToolbar
        filters={filters}
        onChange={handleFiltersChange}
        onClear={handleClear}
      />
      {listError ? (
        <Typography variant="body2" css={tw`bg-shades-0 px-4 py-3 text-critical-500`}>
          {listError}
        </Typography>
      ) : null}
      <UsBankEarningsTable
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        onShowOrders={setOrdersEarning}
      />
      <EarningOrdersModal earning={ordersEarning} onClose={handleCloseOrders} />
    </div>
  )
}

export const UsBankTransactionsPage = memo(UsBankTransactionsPageComponent)
